import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const IconPage: Component = () => {
  const [currentSize, setCurrentSize] = createSignal(48)

  return (
    <>
      <Title>Icon - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Icon</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Sistema visual de glifos y símbolos. El componente Icon proporciona una interfaz unificada para consumir bibliotecas de iconos (Solar Set por defecto) con control total sobre dimensioes,
            colores y accesibilidad.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes de Estilo Solar */}
          <Card>
            <Card.Header
              title="Solar Icons (Default Set)"
              subtitle="Optimizado para un look dual-tone moderno con variaciones de peso y estilo."
              icon={<Icon icon="material-symbols-light:star-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 p-10 bg-surface-muted rounded-2xl border border-border">
                <div class="flex flex-col items-center gap-3">
                  <Icon icon="material-symbols-light:home-outline" size={32} class="text-foreground" />
                  <span class="text-[10px] font-mono text-foreground-subtle">bold-duotone</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Icon icon="material-symbols-light:person-outline" size={32} class="text-primary-600" />
                  <span class="text-[10px] font-mono text-foreground-subtle">bold</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Icon icon="material-symbols-light:settings-outline" size={32} class="text-secondary-600" />
                  <span class="text-[10px] font-mono text-foreground-subtle">linear</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Icon icon="material-symbols-light:notifications-off-outline" size={32} class="text-amber-500" />
                  <span class="text-[10px] font-mono text-foreground-subtle">broken</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Icon icon="material-symbols-light:verified-outline" size={32} class="text-emerald-500" />
                  <span class="text-[10px] font-mono text-foreground-subtle">outline</span>
                </div>
                <div class="flex flex-col items-center gap-3">
                  <Icon icon="material-symbols-light:chat-outline" size={32} class="text-indigo-500" />
                  <span class="text-[10px] font-mono text-foreground-subtle">duotone</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Escalamiento y Color */}
          <Card>
            <Card.Header
              title="Escalamiento e Inyección de Color"
              subtitle="Los iconos heredan el color del texto actual o pueden ser coloreados mediante utilidades de Tailwind."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div class="space-y-6">
                  <h4 class="text-sm font-bold text-foreground-subtle uppercase">Control de Dimensión Manual</h4>
                  <div class="p-10 bg-surface rounded-2xl border border-border flex items-center justify-center min-h-[200px]">
                    <Icon icon="material-symbols-light:favorite-outline" size={currentSize()} class="text-red-500 transition-all duration-300 drop-shadow-lg" />
                  </div>
                  <div class="px-4">
                    <input
                      type="range"
                      min="16"
                      max="120"
                      step="4"
                      value={currentSize()}
                      onInput={e => setCurrentSize(parseInt(e.target.value, 10))}
                      class="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                    />
                    <div class="flex justify-between text-[10px] font-bold text-foreground-subtle mt-2 uppercase">
                      <span>16px</span>
                      <span>{currentSize()}px</span>
                      <span>120px</span>
                    </div>
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="text-sm font-bold text-foreground-subtle uppercase">Utilidades de Color (TW)</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="p-4 bg-primary-soft rounded-xl border border-primary/25 flex items-center gap-3">
                      <Icon icon="material-symbols-light:check-circle-outline" class="text-primary-600" size={24} />
                      <span class="text-xs font-mono">text-primary-600</span>
                    </div>
                    <div class="p-4 bg-success-soft rounded-xl border border-success/25 flex items-center gap-3">
                      <Icon icon="material-symbols-light:cloud-done-outline" class="text-success-600" size={24} />
                      <span class="text-xs font-mono">text-success-600</span>
                    </div>
                    <div class="p-4 bg-danger-soft rounded-xl border border-danger/25 flex items-center gap-3">
                      <Icon icon="material-symbols-light:local-fire-department" class="text-danger-600" size={24} />
                      <span class="text-xs font-mono">text-danger-600</span>
                    </div>
                    <div class="p-4 bg-slate-900 rounded-xl border border-white/5 flex items-center gap-3">
                      <Icon icon="material-symbols-light:star-outline" class="text-amber-400 animate-pulse" size={24} />
                      <span class="text-xs font-mono text-white">text-amber-400</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Especificaciones técnicas del componente Icon." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">icon</td>
                      <td class="px-6 py-4 italic text-xs">string | IconComponent</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Identificador del icono (ej: 'material-symbols-light:home-outline').</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic text-xs">number | string</td>
                      <td class="px-6 py-4 font-mono">24</td>
                      <td class="px-6 py-4">Dimensión en píxeles (width/height igualados).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">width / height</td>
                      <td class="px-6 py-4 italic text-xs">number | string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Control independiente de dimensiones.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">class</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-balance italic">Clases CSS para inyectar colores y efectos Tailwind.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default IconPage
